import { useCallback, useContext, useEffect, useState } from 'react';
import { IApiHooksWithTriggerData, IIdentityObject } from '../../interfaces/app-interfaces';
import { IOrderData } from '../../interfaces/order-tracking-flow-interfaces';
import { getOrderList } from '../../utils/chatbot/order-tracking/response-handlers';
import { ICBOrderListResponse } from '../../interfaces/chatbot-api-interfaces';
import { API_ENDPOINTS } from '../../config/api-config';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { useIdentity } from '@vp/auth-react';
import { getChatbotToken, getRestId } from '../../utils/session-storage-utils';
import { CAContext } from '../../contexts/CAContext';

export interface IOrderDataRequest {
  pageNumber: number;
}

const useCBFetchOrders = (): IApiHooksWithTriggerData<IOrderData[], IOrderDataRequest> => {
  const identity = useIdentity();
  const {
    chatState: { locale },
  } = useContext(CAContext);
  const { logCustomAttributesNR } = useNRLogging();

  const [data, setData] = useState<IOrderData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [requestData, setRequestData] = useState<IOrderDataRequest>();

  const trigger = useCallback((msg: IOrderDataRequest) => {
    setRequestData(msg);
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        if (requestData) {
          const chatbotToken = getChatbotToken();
          const { authorizationHeader, canonicalId } = identity as IIdentityObject;

          const myHeaders = new Headers();
          myHeaders.append('Content-Type', 'application/json');
          myHeaders.append('Authorization', authorizationHeader);
          myHeaders.append('Chatbot-Authorization', `Bearer ${chatbotToken}`);

          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify({
              order_page: requestData.pageNumber.toString(),
              shopper_id: canonicalId || '',
              request_id: getRestId() || '',
            }),
            redirect: 'follow' as RequestRedirect,
          };
          logCustomAttributesNR('ca-chatbot-api-request', {
            logLevel: 'info',
            message: 'Fetching orders from chatbot API',
            pageNumber: requestData.pageNumber,
            apiType: 'getChatbotOrders',
          });
          await fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/orders`, requestOptions)
            .then((response) => {
              if (!response.ok) {
                // If response is not ok (status not in the range 200-299),
                // throw an error to skip the next then block
                const error = new Error(`API responded with status: ${response.status}`);
                error.name = 'ApiError';
                throw error;
              }
              return response.json();
            })
            .then((result) => {
              const orderlist = getOrderList(locale, result as ICBOrderListResponse);
              setData(orderlist);
              setError(undefined);
              logCustomAttributesNR('ca-chatbot-api-response', {
                logLevel: 'info',
                message: 'Fetching orders from chatbot API success',
                apiType: 'getChatbotOrders',
              });
            })
            .catch((error) => {
              setError(error as Error);
              logCustomAttributesNR('ca-chatbot-api-error', {
                errorName: error.name,
                errorStack: error.stack,
                message: error.message,
                logLevel: 'error',
                apiType: 'getChatbotOrders',
              });
            });
        }
      } catch (error) {
        setError(error as Error);
        logCustomAttributesNR('ca-chatbot-api-error', {
          errorName: error.name,
          errorStack: error.stack,
          message: error.message,
          logLevel: 'error',
          apiType: 'getChatbotOrders',
        });
      } finally {
        setLoading(false);
        setRequestData(undefined); // Reset requestData after completion
      }
    };
    fetchData();
  }, [requestData]);
  return {
    data,
    loading,
    error,
    trigger,
  };
};

export { useCBFetchOrders };
